/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/MonacoView.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why              | What Changed                         |
 * |---|------------------------|-------------------|------------------|--------------------------------------|
 * | 1 | 2026-06-18 (Phase 3)   | Claude Sonnet 4.6 | Phase 3 Monaco   | Created — read-only TS code panel    |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (<MonacoView />)
 *
 * PROPS / EXPORTS
 * ---------------
 * MonacoViewProps:
 *   steps  — current recorder steps (readonly)
 *   name   — test name shown in the header
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. Monaco is loaded lazily via React.lazy / dynamic import to keep the
 *    initial bundle small. A loading spinner shows while Monaco initialises.
 * 2. The code is read-only (readOnly: true in Monaco options).
 * 3. irToSdkSource() from @mimiq/sdk generates the TypeScript from IR steps.
 *    It creates a synthetic TestIR to pass to the generator.
 * 4. The panel is collapsible — hidden by default to save space.
 *    User can expand it to see the SDK equivalent of their recorded steps.
 * 5. Copy-to-clipboard button lets SDETs grab the code without using Monaco's
 *    UI which can be awkward on touch / small screens.
 *
 * =============================================================================
 */

import { useState, useMemo, Suspense, lazy } from "react";
import type { IRStep, TestIR } from "@mimiq/ir";
import { generateId, IR_VERSION } from "@mimiq/ir";
import { irToSdkSource } from "@mimiq/sdk";

// Lazy-load Monaco to keep initial bundle small
const MonacoEditor = lazy(() =>
  import("@monaco-editor/react").then((m) => ({ default: m.default }))
);

// ── Types ─────────────────────────────────────────────────────────────────────

export interface MonacoViewProps {
  steps: readonly IRStep[];
  name: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function stepsToIR(steps: readonly IRStep[], name: string): TestIR {
  return {
    irVersion: IR_VERSION,
    meta: { id: generateId(), name, tags: [], createdBy: "studio-recorder" },
    config: {
      targets: ["android"],
      env: {},
      dataSources: {},
      interruptionPolicy: { permissions: "allowAll", crashOnAnr: true },
      healing: { maxTierUsed: 1, minConfidence: 0.75, mode: "suggest" },
      timeouts: { step: 15000 },
    },
    flows: [{ id: generateId(), name: "Flow 1", entry: true, steps: [...steps] }],
    hooks: { beforeEach: [], afterEach: [] },
  };
}

// ── Root component ────────────────────────────────────────────────────────────

export function MonacoView({ steps, name }: MonacoViewProps) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const code = useMemo(() => {
    if (steps.length === 0) return "// No steps recorded yet.";
    try {
      return irToSdkSource(stepsToIR(steps, name || "Recorded Test"));
    } catch {
      return "// Error generating code.";
    }
  }, [steps, name]);

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // silently ignore — clipboard may be blocked
    }
  }

  return (
    <section className="monaco-view">
      <div className="monaco-view-header" onClick={() => setExpanded((e) => !e)}>
        <span className="monaco-view-title">
          {expanded ? "▾" : "▸"} Test Script
        </span>
        {expanded && (
          <button
            className="monaco-copy-btn"
            onClick={(e) => { e.stopPropagation(); void copyCode(); }}
            title="Copy to clipboard"
          >
            {copied ? "✓ Copied" : "Copy"}
          </button>
        )}
      </div>

      {expanded && (
        <div className="monaco-view-body">
          <Suspense fallback={<div className="monaco-loading">Loading editor…</div>}>
            <MonacoEditor
              height="320px"
              language="typescript"
              theme="vs-dark"
              value={code}
              options={{
                readOnly: true,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 12,
                lineNumbers: "on",
                wordWrap: "on",
                folding: false,
                renderLineHighlight: "none",
                overviewRulerLanes: 0,
                scrollbar: { verticalScrollbarSize: 6, horizontalScrollbarSize: 6 },
              }}
            />
          </Suspense>
        </div>
      )}
    </section>
  );
}
